// 版本管理相关API服务
// 根据 Go 后端接口文档实现
import { httpClient } from '../http';
import {
  Version,
  CreateVersionRequest,
  UpdateVersionRequest,
  VersionListParams,
  PaginationResponse,
  PublishVersionRequest,
  PauseVersionRequest,
  ResumeVersionRequest,
  StrategyResponse,
  CreateStrategyRequest,
  UpdateStrategyRequest,
  VersionDiffsResponse,
  VersionDiffsParams,
  TestDevice,
  // 用于灰度推进
  RolloutProgressStat,
} from '@/types/api';

export class VersionsAPI {
  /**
   * 获取版本列表（分页）
   * GET /admin/versions
   * @param params 查询参数：page, size, search, status, model_id
   */
  async getVersions(params?: VersionListParams): Promise<PaginationResponse<Version>> {
    const response = await httpClient.get<PaginationResponse<Version>>('/admin/versions', params);
    return response.data;
  }

  /**
   * 获取版本详情
   * GET /admin/versions/{id}
   * @param id 版本ID
   */
  async getVersionById(id: number): Promise<Version> {
    const response = await httpClient.get<Version>(`/admin/versions/${id}`);
    return response.data;
  }

  /**
   * 创建版本
   * POST /admin/versions
   * @param versionData 创建版本请求数据（file_id, description, force_update）
   * 注意：后端会从S3下载文件解析version.txt，大文件需要较长超时
   */
  async createVersion(versionData: CreateVersionRequest): Promise<Version> {
    const response = await httpClient.post<Version>('/admin/versions', versionData, undefined, {
      timeout: 10 * 60 * 1000, // 10分钟超时，适配大文件处理
    });
    return response.data;
  }

  /**
   * 更新版本
   * PUT /admin/versions/{id}
   * @param id 版本ID
   * @param versionData 更新版本请求数据（description）
   */
  async updateVersion(id: number, versionData: UpdateVersionRequest): Promise<Version> {
    const response = await httpClient.put<Version>(`/admin/versions/${id}`, versionData);
    return response.data;
  }

  /**
   * 获取版本差分关系列表
   * GET /admin/versions/diffs
   * @param params 查询参数：model_id, to_version
   */
  async getVersionDiffs(params: VersionDiffsParams): Promise<VersionDiffsResponse> {
    const response = await httpClient.get<VersionDiffsResponse>('/admin/versions/diffs', params);
    return response.data;
  }

  /**
   * 获取发布策略
   * GET /admin/versions/{id}/strategy
   * @param versionId 版本ID
   */
  async getStrategy(versionId: number): Promise<StrategyResponse> {
    const response = await httpClient.get<StrategyResponse>(`/admin/versions/${versionId}/strategy`);
    return response.data;
  }

  /**
   * 创建发布策略
   * POST /admin/versions/strategies
   * @param strategyData 创建策略请求数据
   */
  async createStrategy(strategyData: CreateStrategyRequest): Promise<StrategyResponse> {
    const response = await httpClient.post<StrategyResponse>('/admin/versions/strategies', strategyData);
    return response.data;
  }

  /**
   * 更新发布策略
   * PUT /admin/versions/{id}/strategy
   * @param versionId 版本ID
   * @param strategyData 更新策略请求数据
   */
  async updateStrategy(versionId: number, strategyData: UpdateStrategyRequest): Promise<StrategyResponse> {
    const response = await httpClient.put<StrategyResponse>(`/admin/versions/${versionId}/strategy`, strategyData);
    return response.data;
  }

  /**
   * 删除发布策略
   * DELETE /admin/versions/{id}/strategy
   * @param versionId 版本ID
   */
  async deleteStrategy(versionId: number): Promise<void> {
    await httpClient.delete(`/admin/versions/${versionId}/strategy`);
  }

  /**
   * 发布版本
   * POST /admin/versions/publish
   * @param request 发布请求（version_id）
   */
  async publishVersion(request: PublishVersionRequest): Promise<void> {
    await httpClient.post('/admin/versions/publish', request);
  }

  /**
   * 暂停版本发布
   * POST /admin/versions/pause
   * @param request 暂停请求（version_id）
   */
  async pauseVersion(request: PauseVersionRequest): Promise<void> {
    await httpClient.post('/admin/versions/pause', request);
  }

  /**
   * 恢复版本发布
   * POST /admin/versions/resume
   * @param request 恢复请求（version_id）
   */
  async resumeVersion(request: ResumeVersionRequest): Promise<void> {
    await httpClient.post('/admin/versions/resume', request);
  }

  /**
   * 删除版本
   * DELETE /admin/versions/{id}
   * @param id 版本ID
   */
  async deleteVersion(id: number): Promise<void> {
    await httpClient.delete(`/admin/versions/${id}`);
  }

  /**
   * 为指定版本设置测试设备列表（按设备SN绑定，覆盖式）
   * POST /admin/versions/{id}/test-devices
   * 后端期望收到 device_sns: string[]，会先清空旧数据再写入新列表
   */
  async setTestDevices(versionId: number, deviceSNs: string[]): Promise<void> {
    await httpClient.post(`/admin/versions/${versionId}/test-devices`, {
      device_sns: deviceSNs,
    });
  }

  /**
   * 获取指定版本的测试设备列表
   * GET /admin/versions/{id}/test-devices
   */
  async getTestDevices(versionId: number): Promise<TestDevice[]> {
    const response = await httpClient.get<TestDevice[]>(`/admin/versions/${versionId}/test-devices`);
    return response.data;
  }

  /**
   * 手动推进灰度到下一个阶段
   * POST /admin/versions/rollout/advance
   */
  async advanceRollout(versionId: number): Promise<RolloutProgressStat> {
    const response = await httpClient.post<RolloutProgressStat>('/admin/versions/rollout/advance', {
      version_id: versionId,
    });
    return response.data;
  }
}

// 导出单例实例
export const versionsAPI = new VersionsAPI();
